import React, { useState } from 'react';

export const FormField = ({ label, error, required, children }) => (
  <div className="space-y-1">
    {label && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>}
    {children}
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

export const useForm = (initialValues, validationRules = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validate = (vals = values) => {
    const errs = {};
    Object.entries(validationRules).forEach(([field, rules]) => {
      if (rules.required && !vals[field]) errs[field] = `${field} is required`;
      else if (rules.mobile && vals[field] && !/^\d{10}$/.test(vals[field])) errs[field] = 'Mobile number must be exactly 10 digits';
      else if (rules.email && vals[field] && !/\S+@\S+\.\S+/.test(vals[field])) errs[field] = 'Invalid email address';
      else if (rules.min && vals[field] && vals[field].length < rules.min) errs[field] = `Minimum ${rules.min} characters`;
      else if (rules.pattern && vals[field] && !rules.pattern.test(vals[field])) errs[field] = rules.message || 'Invalid format';
    });
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const rule = validationRules[name];
    if (rule?.mobile && /\D/.test(value)) return; // non-digit block
    if (rule?.mobile && value.length > 10) return;  // max 10 digits
    setValues(v => ({ ...v, [name]: value }));
    if (touched[name]) setErrors(errs => ({ ...errs, [name]: validate({ ...values, [name]: value })[name] }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(t => ({ ...t, [name]: true }));
    setErrors(errs => ({ ...errs, [name]: validate()[name] }));
  };

  const handleSubmit = (onSubmit) => (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    setTouched(Object.keys(validationRules).reduce((a, k) => ({ ...a, [k]: true }), {}));
    if (!Object.keys(errs).length) onSubmit(values);
  };

  const reset = (newValues = initialValues) => { setValues(newValues); setErrors({}); setTouched({}); };

  return { values, errors, handleChange, handleBlur, handleSubmit, reset, setValues };
};

const Form = ({ onSubmit, children, className = '' }) => (
  <form onSubmit={onSubmit} className={`space-y-4 ${className}`} noValidate>{children}</form>
);

export default Form;
